import { makeAuthenticatedRequest } from './authService';
import { logApiConfig } from '../config/api';

// Log API configuration in development
if (process.env.NODE_ENV === 'development') {
  logApiConfig();
}

export interface CashflowEntry {
  id: string;
  created_at: string;
  created_by: string;
  amount: number;
  account_receive?: string;
  account_subtract?: string;
  category?: string;
  sub_category?: string;
  tenant: string;
  notes?: string;
}

export interface CreateCashflowData {
  id?: string;
  amount: number;
  account_receive?: string;
  account_subtract?: string;
  category?: string;
  sub_category?: string;
  tenant?: string;
  notes?: string;
  created_by?: string;
  created_at?: string;
}

export interface UpdateCashflowData {
  amount?: number;
  account_receive?: string;
  account_subtract?: string;
  category?: string;
  sub_category?: string;
  notes?: string;
  created_at?: string;
}

export interface CashflowFilters {
  start_date?: string;
  end_date?: string;
  category?: string;
  exclude_categories?: string | string[];
  limit?: number;
  offset?: number;
}

export interface CashflowSummary {
  period: string;
  total: number;
}

export interface CashflowSummaryResponse {
  success: boolean;
  type: string;
  period: string;
  summary: CashflowSummary[];
}

class CashflowService {
  // Get all cashflow entries
  async getCashflowEntries(filters?: CashflowFilters): Promise<{ success: boolean; cashflow?: CashflowEntry[]; count?: number; message?: string }> {
    try {
      const queryParams = new URLSearchParams();
      if (filters?.start_date) queryParams.append('start_date', filters.start_date);
      if (filters?.end_date) queryParams.append('end_date', filters.end_date);
      if (filters?.category) queryParams.append('category', filters.category);
      if (filters?.exclude_categories) {
        const excludeCategories = Array.isArray(filters.exclude_categories)
          ? filters.exclude_categories.join(',')
          : filters.exclude_categories;
        queryParams.append('exclude_categories', excludeCategories);
      }
      if (filters?.limit) queryParams.append('limit', filters.limit.toString());
      if (filters?.offset) queryParams.append('offset', filters.offset.toString());
      
      const queryString = queryParams.toString();
      const url = `/cashflow${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching cashflow entries:', error);
      throw error;
    }
  }

  // Get cashflow entry by ID
  async getCashflowById(id: string): Promise<{ success: boolean; cashflow?: CashflowEntry; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/cashflow/${id}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching cashflow entry:', error);
      throw error;
    }
  }

  // Create a new cashflow entry
  async createCashflowEntry(data: CreateCashflowData): Promise<{ success: boolean; id?: string; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest('/cashflow', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      
      return response;
    } catch (error) {
      console.error('Error creating cashflow entry:', error);
      throw error;
    }
  }

  // Update a cashflow entry
  async updateCashflowEntry(id: string, data: UpdateCashflowData): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/cashflow/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
      });
      
      return response;
    } catch (error) {
      console.error('Error updating cashflow entry:', error);
      throw error;
    }
  }

  // Delete a cashflow entry
  async deleteCashflowEntry(id: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/cashflow/${id}`, {
        method: 'DELETE'
      });
      
      return response;
    } catch (error) {
      console.error('Error deleting cashflow entry:', error);
      throw error;
    }
  }

  // Get cashflow summary
  async getCashflowSummary(type: 'spent' | 'income' | 'investment', period: 'month' | 'year' = 'month'): Promise<CashflowSummaryResponse> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('type', type);
      queryParams.append('period', period);
      
      const response = await makeAuthenticatedRequest(`/cashflow/summary?${queryParams.toString()}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching cashflow summary:', error);
      throw error;
    }
  }

  // Get unique categories
  async getCategories(): Promise<{ success: boolean; categories?: string[]; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/cashflow/categories`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }

  // Get unique sub-categories
  async getSubCategories(category?: string): Promise<{ success: boolean; sub_categories?: string[]; message?: string }> {
    try {
      const queryParams = new URLSearchParams();
      if (category) {
        queryParams.append('category', category);
      }
      
      const queryString = queryParams.toString();
      const url = `/cashflow/sub-categories${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching sub-categories:', error);
      throw error;
    }
  }
}

export const cashflowService = new CashflowService();

